import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useThreadStore } from "@/store";
import { Link, Linkedin, X } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { getContentWithSources } from "../utils/citationFormatter";

// Custom SVG Icon
const TwitterXIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const ShareDialog = ({
  open,
  onOpenChange,
  content,
  title,
  sources,
}) => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 640
  );
  const { currentThread } = useThreadStore();

  // Handle window resize for responsive truncation
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Handle copy link
  const handleCopyLink = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
    }
  };

  // Handle social media sharing
  const handleSocialShare = (platform, text) => {
    const encodedText = encodeURIComponent(text);

    let shareUrl = "";

    switch (platform) {
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;
        break;
      case "reddit":
        shareUrl = `https://reddit.com/submit?title=${encodedText}&text=${encodedText}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  // Truncate content for display - responsive truncation
  const getResponsiveTruncation = () => {
    if (!content) return "";

    // Use different truncation lengths based on screen size
    const isMobile = windowWidth < 640; // sm breakpoint
    const maxLength = isMobile ? 120 : 250;

    return content.length > maxLength
      ? content.substring(0, maxLength) + "..."
      : content;
  };

  const truncatedContent = getResponsiveTruncation();
  const displayTitle = currentThread?.title || "AI Response";

  // Get content with sources for copying/sharing
  const contentWithSources = sources
    ? getContentWithSources(content, sources)
    : content;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] sm:max-w-md md:max-w-lg lg:max-w-xl p-0 bg-[#1a1a1a] border-0 rounded-2xl"
        style={{ zIndex: 9999 }}
        hideCloseButton={true}
      >
        <div className="relative">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 h-7 w-7 sm:h-8 sm:w-8 text-white bg-transparent hover:bg-white/10"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>

          {/* Header */}
          <div className="p-4 sm:p-6 pb-3 sm:pb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-white break-words">
              {displayTitle.split(" ").slice(0, 5).join(" ") +
                (displayTitle.split(" ").length > 5 ? "..." : "")}
            </h2>
          </div>

          {/* Text Content */}
          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="bg-[#2a2a2a] rounded-lg p-3 sm:p-4 border border-gray-600 max-h-[40vh] overflow-y-auto">
              <div className="text-gray-300 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words">
                {truncatedContent}
              </div>
            </div>
          </div>

          {/* Share Options */}
          <div className="px-4 sm:px-6 pb-4 sm:pb-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {/* Copy Link */}
              <button
                onClick={() => handleCopyLink(contentWithSources)}
                className="flex items-center justify-center p-2 sm:p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-transparent flex items-center justify-center">
                  <Link className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </button>

              {/* Twitter/X */}
              <button
                onClick={() => handleSocialShare("twitter", contentWithSources)}
                className="flex items-center justify-center p-2 sm:p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-transparent flex items-center justify-center">
                  <TwitterXIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </button>

              {/* LinkedIn */}
              <button
                onClick={() =>
                  handleSocialShare("linkedin", contentWithSources)
                }
                className="flex items-center justify-center p-2 sm:p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-transparent flex items-center justify-center">
                  <Linkedin className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </button>

              {/* Reddit */}
              <button
                onClick={() => handleSocialShare("reddit", contentWithSources)}
                className="flex items-center justify-center p-2 sm:p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-transparent flex items-center justify-center">
                  <svg
                    className="h-5 w-5 sm:h-6 sm:w-6 text-white"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                  </svg>
                </div>
              </button>
            </div>

            {/* Labels */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center">
                <span className="text-xs text-gray-400">Copy link</span>
              </div>
              <div className="text-center">
                <span className="text-xs text-gray-400">X</span>
              </div>
              <div className="text-center">
                <span className="text-xs text-gray-400">LinkedIn</span>
              </div>
              <div className="text-center">
                <span className="text-xs text-gray-400">Reddit</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

ShareDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  content: PropTypes.string.isRequired,
  title: PropTypes.string,
};

export default ShareDialog;
